import { useMemo, useState } from "react";
import {
  BaseTable,
  ColumnFiltersState,
  LeafyGreenTable,
  SortingState,
  TableControl,
  TablePlaceholder,
  TableWrapper,
  onChangeHandler,
  useLeafyGreenTable,
} from "@evg-ui/lib/components/Table";
import { useQueryParams } from "@evg-ui/lib/hooks";
import { getLocalStorageBoolean } from "@evg-ui/lib/utils/localStorage";
import { useVersionAnalytics } from "analytics";
import { getColumnsTemplate } from "components/TasksTable/Columns";
import { TaskTableInfo } from "components/TasksTable/types";
import { DISABLE_TASK_REVIEW } from "constants/cookies";
import { TableQueryParams } from "constants/queryParams";
import { SortDirection, TaskSortCategory } from "gql/generated/types";
import { useTableSort, useTaskStatuses } from "hooks";
import { PatchTasksQueryParams } from "types/task";
import { parseSortString } from "utils/queryString";
import {
  defaultSorting,
  emptyFilterQueryParams,
  mapIdToFilterParam,
} from "./constants";

// Create a more specific enum because duration is not a valid category to sort / filter by in the tasks table.
enum VersionTaskCategory {
  BaseStatus = TaskSortCategory.BaseStatus,
  Name = TaskSortCategory.Name,
  Status = TaskSortCategory.Status,
  Variant = TaskSortCategory.Variant,
}

interface VersionTasksTableQueryParams {
  [PatchTasksQueryParams.TaskName]?: string;
  [PatchTasksQueryParams.Statuses]?: string | string[];
  [PatchTasksQueryParams.BaseStatuses]?: string | string[];
  [PatchTasksQueryParams.Variant]?: string;
  [TableQueryParams.Sorts]?: string | string[];
}

interface VersionTasksTableProps {
  clearQueryParams: () => void;
  filteredCount: number;
  isPatch: boolean;
  limit: number;
  loading: boolean;
  page: number;
  tasks: TaskTableInfo[];
  totalCount: number;
  versionId: string;
}

export const VersionTasksTable: React.FC<VersionTasksTableProps> = ({
  clearQueryParams,
  filteredCount,
  isPatch,
  limit,
  loading,
  page,
  tasks,
  totalCount,
  versionId,
}) => {
  const [queryParams, setQueryParams] = useQueryParams();
  const { sendEvent } = useVersionAnalytics(versionId);
  const taskReviewEnabled = !getLocalStorageBoolean(DISABLE_TASK_REVIEW, false);

  const { baseStatuses: baseStatusOptions, currentStatuses: statusOptions } =
    useTaskStatuses({ versionId });

  const { initialFilters, initialSorting } = getInitialState(queryParams);

  const columns = useMemo(
    () =>
      getColumnsTemplate({
        baseStatusOptions,
        statusOptions,
        isPatch,
        onClickTaskLink: (taskId: string, status?: string) =>
          sendEvent({
            name: "Clicked task table task link",
            "task.id": taskId,
            "task.status": status ?? "",
          }),
        onClickTaskStatusBadge: (
          taskId: string,
          status: string,
          column: string,
        ) =>
          sendEvent({
            name: "Clicked task table status badge",
            "task.id": taskId,
            "task.status": status,
            column: column,
          }),
      }),
    [baseStatusOptions, statusOptions, isPatch, sendEvent],
  );

  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(initialFilters);

  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const updateFilters = (filterState: ColumnFiltersState) => {
    const updatedParams: Record<string, unknown> = {
      ...queryParams,
      page: "0",
      ...emptyFilterQueryParams,
    };
    filterState.forEach(({ id, value }) => {
      const key = mapIdToFilterParam[id as VersionTaskCategory];
      updatedParams[key] = value;
    });
    setQueryParams(updatedParams);
    sendEvent({
      name: "Filtered tasks table",
      "filter.by": Object.keys(filterState),
    });
  };

  const updateSorting = useTableSort({
    sendAnalyticsEvents: (sorter: SortingState) =>
      sendEvent({
        name: "Sorted tasks table",
        "sort.by": sorter.map(({ id }) => id as TaskSortCategory),
      }),
  });

  const table: LeafyGreenTable<TaskTableInfo> =
    useLeafyGreenTable<TaskTableInfo>({
      columns,
      data: tasks ?? [],
      defaultColumn: {
        enableMultiSort: true,
        // Handle bug in sorting order (https://github.com/TanStack/table/issues/4289)
        sortDescFirst: false,
      },
      getRowId: (originalRow, _index, parentRow) =>
        parentRow ? `${parentRow.id}.${originalRow.id}` : originalRow.id,
      initialState: {
        columnVisibility: { reviewed: taskReviewEnabled },
      },
      isMultiSortEvent: () => true, // Override default requirement for shift-click to multisort.
      state: {
        columnFilters,
        sorting,
      },
      maxMultiSortColCount: 2,
      manualFiltering: true,
      manualPagination: true,
      manualSorting: true,
      onColumnFiltersChange: onChangeHandler<ColumnFiltersState>(
        setColumnFilters,
        (updatedState) => updateFilters(updatedState),
      ),
      onSortingChange: onChangeHandler<SortingState>(setSorting, updateSorting),
      getSubRows: (row) => row.executionTasksFull || [],
    });

  return (
    <TableWrapper
      controls={
        <TableControl
          filteredCount={filteredCount}
          limit={limit}
          loading={loading}
          onClear={() => {
            setColumnFilters([]);
            setSorting(defaultSorting);
            clearQueryParams();
          }}
          onPageSizeChange={(size: number) =>
            sendEvent({ name: "Changed page size", "page.size": size })
          }
          page={page}
          totalCount={totalCount}
        />
      }
      shouldShowBottomTableControl={limit > 10}
    >
      <BaseTable
        data-loading={loading}
        data-testid="tasks-table"
        data-testid-row="tasks-table-row"
        emptyComponent={<TablePlaceholder message="No tasks found." />}
        loading={loading}
        loadingRows={limit}
        shouldAlternateRowColor
        table={table}
      />
    </TableWrapper>
  );
};

export const getInitialState = (
  queryParams: VersionTasksTableQueryParams,
): {
  initialFilters: ColumnFiltersState;
  initialSorting: SortingState;
} => {
  const taskName = queryParams[PatchTasksQueryParams.TaskName];
  const statuses = queryParams[PatchTasksQueryParams.Statuses];
  const baseStatuses = queryParams[PatchTasksQueryParams.BaseStatuses];
  const variant = queryParams[PatchTasksQueryParams.Variant];
  const sorts = queryParams[TableQueryParams.Sorts];

  const initialFilters = [];

  if (taskName) {
    initialFilters.push({
      id: VersionTaskCategory.Name,
      value: taskName,
    });
  }
  if (statuses) {
    initialFilters.push({
      id: VersionTaskCategory.Status,
      value: Array.isArray(statuses) ? statuses : [statuses],
    });
  }
  if (baseStatuses) {
    initialFilters.push({
      id: VersionTaskCategory.BaseStatus,
      value: Array.isArray(baseStatuses) ? baseStatuses : [baseStatuses],
    });
  }
  if (variant) {
    initialFilters.push({ id: VersionTaskCategory.Variant, value: variant });
  }

  const parsedSorts = sorts
    ? parseSortString(sorts, {
        sortByKey: "sortCategory",
        sortDirKey: "direction",
        sortCategoryEnum: VersionTaskCategory,
      })
    : [];

  return {
    initialSorting: parsedSorts.length
      ? parsedSorts.map(({ direction, sortCategory }) => ({
          id: sortCategory,
          desc: direction === SortDirection.Desc,
        }))
      : defaultSorting,
    initialFilters,
  };
};
